/**
 * Content of iterative review tab.
 */
import {
    FC,
    useContext,
    useMemo,
} from 'react'

import { TableLoading } from '~/apps/admin/src/lib'
import { IsRemovingType } from '~/apps/admin/src/lib/models'

import { ChallengeDetailContextModel, SubmissionInfo } from '../../models'
import { isContestSubmissionType } from '../../constants'
import {
    isFirst2FinishChallenge as detectFirst2FinishChallenge,
    resolveFirst2FinishIterativeSubmissionIds,
} from '../../utils/challenge'
import { TableNoRecord } from '../TableNoRecord'
import { TableIterativeReview } from '../TableIterativeReview'
import { useRole, useRoleProps } from '../../hooks'
import {
    REVIEWER,
    SUBMITTER,
} from '../../../config/index.config'
import { ChallengeDetailContext } from '../../contexts'
import { hasSubmitterPassedThreshold } from '../../utils/reviewScoring'

import {
    filterIterativeReviewRows,
    getIterativeReviewSubmissionPriority,
    limitFirst2FinishIterativeRows,
} from './iterativeReviewFiltering'

interface Props {
    reviews: SubmissionInfo[]
    submitterReviews: SubmissionInfo[]
    postMortemMinimumPassingScore?: number | null
    isLoadingReview: boolean
    isDownloading: IsRemovingType
    downloadSubmission: (submissionId: string) => void
    isActiveChallenge: boolean
    columnLabel?: string
    phaseIdFilter?: string
    aiReviewers?: { aiWorkflowId: string }[]
}

export const TabContentIterativeReview: FC<Props> = (props: Props) => {
    const {
        aiReviewDecisionsBySubmissionId,
        challengeInfo,
        challengeSubmissions,
        myResources = [],
        resources,
    }: ChallengeDetailContextModel = useContext(ChallengeDetailContext)
    const {
        actionChallengeRole,
        isPrivilegedRole,
        postMortemReviewerResourceIds,
    }: useRoleProps = useRole()
    const hideHandleColumn = props.isActiveChallenge
        && actionChallengeRole === REVIEWER

    const myMemberIds = useMemo<Set<string>>(
        () => new Set((myResources ?? []).map(resource => resource.memberId)),
        [myResources],
    )
    const myResourceIds = useMemo<Set<string>>(
        () => new Set((myResources ?? []).map(resource => resource.id)),
        [myResources],
    )

    const isChallengeCompleted = useMemo(
        () => {
            const normalizedStatus = (challengeInfo?.status ?? '').toUpperCase()
            return normalizedStatus === 'COMPLETED'
                || normalizedStatus === 'CANCELLED'
                || normalizedStatus.startsWith('CANCELLED_')
        },
        [challengeInfo?.status],
    )

    const normalizedColumnLabel = useMemo(
        () => (props.columnLabel ?? '')
            .toLowerCase()
            .replace(/[^a-z]/g, ''),
        [props.columnLabel],
    )
    const isPostMortemPhase = useMemo(
        () => normalizedColumnLabel === 'postmortem',
        [normalizedColumnLabel],
    )
    const isFirst2FinishChallenge = useMemo(
        () => detectFirst2FinishChallenge(challengeInfo),
        [challengeInfo?.track?.name, challengeInfo?.type?.name],
    )
    const first2FinishSubmissionIds = useMemo<string[]>(() => {
        if (isPostMortemPhase || !isFirst2FinishChallenge) {
            return []
        }

        const contestSubmissions = (challengeSubmissions ?? []).filter(submission => isContestSubmissionType(
            submission.type,
            { defaultToContest: true },
        ))
        if (!contestSubmissions.length) {
            return []
        }

        return resolveFirst2FinishIterativeSubmissionIds(
            contestSubmissions,
        )
    }, [
        challengeSubmissions,
        isFirst2FinishChallenge,
        isPostMortemPhase,
    ])

    const isSubmitterOnly = actionChallengeRole === SUBMITTER
        && postMortemReviewerResourceIds.size === 0
    const sourceRows = isPostMortemPhase && isSubmitterOnly
        ? props.submitterReviews
        : props.reviews

    const hasPassedPostMortemThreshold = useMemo(
        () => hasSubmitterPassedThreshold(
            sourceRows ?? [],
            myMemberIds,
            props.postMortemMinimumPassingScore,
        ),
        [sourceRows, myMemberIds, props.postMortemMinimumPassingScore],
    )

    const filteredRows = useMemo(() => {
        const rows = filterIterativeReviewRows({
            aiReviewDecisionsBySubmissionId,
            challengePhases: challengeInfo?.phases,
            isPostMortemPhase,
            limitToSubmissionIds: first2FinishSubmissionIds,
            phaseIdFilter: props.phaseIdFilter,
            reviewerResources: resources,
            sourceRows,
        })

        return rows
    }, [
        aiReviewDecisionsBySubmissionId,
        sourceRows,
        isPostMortemPhase,
        challengeInfo?.phases,
        props.phaseIdFilter,
        resources,
        first2FinishSubmissionIds,
    ])

    const reviewRows = useMemo(() => {
        const map = new Map<string, SubmissionInfo>()

        filteredRows.forEach(submission => {
            const existing = map.get(submission.id)

            if (!existing) {
                map.set(submission.id, submission)
                return
            }

            if (
                getIterativeReviewSubmissionPriority(submission, myResourceIds)
                > getIterativeReviewSubmissionPriority(existing, myResourceIds)
            ) {
                map.set(submission.id, submission)
            }
        })

        return Array.from(map.values())
    }, [filteredRows, myResourceIds])
    const first2FinishReviewRows = useMemo<SubmissionInfo[]>(
        () => {
            if (isPostMortemPhase || !isFirst2FinishChallenge) {
                return reviewRows
            }

            return limitFirst2FinishIterativeRows(reviewRows, first2FinishSubmissionIds, {
                forceSingleRow: true,
            })
        },
        [
            first2FinishSubmissionIds,
            isFirst2FinishChallenge,
            isPostMortemPhase,
            reviewRows,
        ],
    )

    const filteredReviewRows = useMemo<SubmissionInfo[]>(
        () => {
            if (!isPostMortemPhase) {
                return first2FinishReviewRows
            }

            if (
                isPrivilegedRole
                || (isChallengeCompleted && (!isPostMortemPhase || hasPassedPostMortemThreshold))
            ) {
                return first2FinishReviewRows
            }

            return first2FinishReviewRows.filter(row => {
                if (row.review?.resourceId
                    && postMortemReviewerResourceIds.has(row.review.resourceId)) {
                    return true
                }

                if (row.memberId && myMemberIds.has(row.memberId)) {
                    return true
                }

                return false
            })
        },
        [
            first2FinishReviewRows,
            isPostMortemPhase,
            isPrivilegedRole,
            isChallengeCompleted,
            hasPassedPostMortemThreshold,
            postMortemReviewerResourceIds,
            myMemberIds,
        ],
    )

    if (props.isLoadingReview) {
        return <TableLoading />
    }

    if (!filteredReviewRows.length) {
        const emptyMessage = isPostMortemPhase
            ? 'No post-mortem reviews yet'
            : 'No iterative reviews yet'
        return <TableNoRecord message={emptyMessage} />
    }

    const shouldHideSubmissionColumn = isPostMortemPhase

    return (
        <TableIterativeReview
            datas={filteredReviewRows}
            isDownloading={props.isDownloading}
            downloadSubmission={props.downloadSubmission}
            hideHandleColumn={hideHandleColumn}
            columnLabel={props.columnLabel}
            hideSubmissionColumn={shouldHideSubmissionColumn}
            isChallengeCompleted={isChallengeCompleted}
            hasPassedThreshold={hasPassedPostMortemThreshold}
            aiReviewers={props.aiReviewers}
        />
    )
}

export default TabContentIterativeReview
